import * as React from "react"
import * as styles from "./styles.css"
import { Badge, ToggleCheckbox } from "gatsby-interface"
import { Loading } from "gatsby-ui/src/components"
import { clsx } from "clsx"
import {
  useToggleMerlinSourcingEnabledMutation,
  useToggleMerlinGraphQlApiEnabledMutation,
} from "./mutations.generated"
import {
  MerlinSourcingBySiteDocument,
  MerlinSourcingBySiteQuery,
  MerlinSourcingBySiteQueryVariables,
  useMerlinSourcingDetailsQuery,
} from "../details/queries.generated"
import { DataLayerApiConfirmation } from "./data-layer-api-confirmation"
import { useTriggerErrorAlert } from "../shared/error-alert"
import { InfoLink } from "../shared/info-link"
import { MachinePricingTier } from "@gatsbyjs/graphql/types"
import { useFlags } from "launchdarkly-react-client-sdk"
import { Link } from "@gatsbyjs/reach-router"
import { useGatsbyDependenciesForSiteQuery } from "./queries.generated"
import * as semver from "semver"
import { GATSBY_VERSION_FOR_DATA_LAYER } from "../../constants/data-sources"

export type DataLayerProps = {
  siteId: string
  organizationId: string
  permissionToEdit: boolean
}

export function DataLayerSection({
  siteId,
  organizationId,
  permissionToEdit,
}: DataLayerProps) {
  const flags = useFlags()

  const [isApiConfirmationModalOpen, setIsApiConfirmationModalOpen] =
    React.useState(false)

  const sourcing = useMerlinSourcingDetailsQuery({
    variables: {
      siteId,
      organizationId,
    },
  })

  const gatsbyDependencies = useGatsbyDependenciesForSiteQuery({
    variables: { siteId },
  })

  const isCompatibleGatsbyVersion = React.useMemo(
    () =>
      gatsbyDependencies?.data?.gatsbyDependenciesForSite?.some(
        item =>
          item.name === "gatsby" &&
          (semver.satisfies(
            semver.coerce(item.installedVersion) || "",
            GATSBY_VERSION_FOR_DATA_LAYER
          ) ||
            !/\d/.test(item.installedVersion || "") ||
            semver.prerelease(item.installedVersion || ""))
      ),
    []
  )

  const siteName = sourcing.data?.siteDetails?.publicName
  const isFreePlan =
    sourcing?.data?.organizationDetails?.billing?.plan?.baseFeatures?.tier ===
    MachinePricingTier.Free

  const dataLayerIsEnabled = Boolean(
    sourcing.data?.siteDetails?.merlinProductionSourcingEnabled
  )

  const apiIsEnabled = Boolean(
    sourcing.data?.siteDetails?.merlinGraphQLApiEnabled
  )

  // checkbox is disabled (not editable) if the user has no permission or GraphQL API feature is active or nas not granted Early Access or  Gatsby version is incompatible
  const dataLayerToggleDisabled =
    !permissionToEdit ||
    apiIsEnabled ||
    !flags.acceleratedSourcingEarlyAccess ||
    isFreePlan ||
    !isCompatibleGatsbyVersion

  // checkbox is disabled (not editable) if the user has no permission or Data Layer feature is not active or Gatsby version is incompatible
  const apiToggleDisabled =
    !permissionToEdit || !dataLayerIsEnabled || !isCompatibleGatsbyVersion

  const [toggleDataLayer, { loading: toggleDataLayerLoading }] =
    useToggleMerlinSourcingEnabledMutation()
  const [toggleDataLayerApi, { loading: toggleApiLoading }] =
    useToggleMerlinGraphQlApiEnabledMutation()

  const [setError, errorAlert] = useTriggerErrorAlert()

  const handleDataLayerToggle = () => {
    setError(null)

    if (toggleDataLayerLoading) {
      return
    }

    toggleDataLayer({
      variables: {
        siteId,
      },
      update: (proxy, { data }) => {
        if (!data?.toggleMerlinSourcingEnabled?.success) {
          if (data?.toggleMerlinSourcingEnabled?.message) {
            setError({ message: data.toggleMerlinSourcingEnabled.message })
          }

          return
        }

        const queryInfo = {
          query: MerlinSourcingBySiteDocument,
          variables: {
            siteId,
          },
        }

        const queryData = proxy.readQuery<
          MerlinSourcingBySiteQuery,
          MerlinSourcingBySiteQueryVariables
        >(queryInfo)

        if (queryData?.siteDetails) {
          queryData.siteDetails.merlinProductionSourcingEnabled =
            !queryData.siteDetails.merlinProductionSourcingEnabled

          proxy.writeQuery<
            MerlinSourcingBySiteQuery,
            MerlinSourcingBySiteQueryVariables
          >({
            ...queryInfo,
            data: queryData,
          })
        }
      },
      optimisticResponse: {
        toggleMerlinSourcingEnabled: {
          success: true,
          message: null,
          __typename: `MutationResult`,
        },
      },
    }).catch(err => setError(err))
  }

  const toggleApi = () =>
    toggleDataLayerApi({
      variables: {
        siteId,
      },
      update: (proxy, { data }) => {
        if (!data?.toggleMerlinGraphQLApiEnabled?.success) {
          if (data?.toggleMerlinGraphQLApiEnabled?.message) {
            setError({ message: data.toggleMerlinGraphQLApiEnabled.message })
            return setIsApiConfirmationModalOpen(false)
          }
        }

        const queryInfo = {
          query: MerlinSourcingBySiteDocument,
          variables: {
            siteId,
          },
        }

        const queryData = proxy.readQuery<
          MerlinSourcingBySiteQuery,
          MerlinSourcingBySiteQueryVariables
        >(queryInfo)

        setIsApiConfirmationModalOpen(false)

        if (queryData?.siteDetails) {
          queryData.siteDetails.merlinGraphQLApiEnabled =
            !queryData.siteDetails.merlinGraphQLApiEnabled

          proxy.writeQuery<
            MerlinSourcingBySiteQuery,
            MerlinSourcingBySiteQueryVariables
          >({
            ...queryInfo,
            data: queryData,
          })
        }
      },
      optimisticResponse: {
        toggleMerlinGraphQLApiEnabled: {
          success: true,
          message: null,
          __typename: `MutationResult`,
        },
      },
    }).catch(err => {
      setIsApiConfirmationModalOpen(false)
      setError(err)
    })

  const handleApiToggle = () => {
    setError(null)

    if (toggleApiLoading) {
      return
    }

    if (apiIsEnabled) {
      return setIsApiConfirmationModalOpen(true)
    }

    toggleApi()
  }

  const noPermissionsMsg = `You do not have enough permissions. Please contact the workspace admin.`

  return (
    <>
      <div
        className={clsx(
          styles.card,
          !flags.acceleratedSourcingEarlyAccess && styles.contentHubCard,
          flags.acceleratedSourcingEarlyAccess &&
            styles.earlyAccessSContentHubCard
        )}
        id="content-hub"
      >
        <h3 className={styles.cardHeading}>
          Content Hub
          <Badge
            className={styles.cardHeadingBadge}
            textVariant="DEFAULT"
            tone="WARNING"
          >
            Early Access
          </Badge>
        </h3>

        {sourcing.loading ? (
          <Loading />
        ) : (
          <>
            <ToggleCheckbox
              id="enable-data-layer-checkbox"
              label={
                <span className={styles.toggleLabel}>
                  Enable Content Hub
                  <InfoLink
                    url="/docs/reference/cloud/valhalla-content-hub"
                    title="Learn about Content Hub"
                  />
                </span>
              }
              hint={
                permissionToEdit ? (
                  <>
                    <p className={styles.hint}>
                      Branch builds and Pull Request builds source up to 100x
                      faster by sourcing once and storing your data in the
                      Cloud.
                    </p>

                    <p className={styles.hint}>
                      Content Hub currently works out-of-the-box with selected
                      Gatsby Source Plugins: The corresponding data sources are
                      highlighted with a <strong>Content Hub ready</strong>{" "}
                      badge in the list below. Please refer to the{" "}
                      <a
                        href="/docs/reference/cloud/valhalla-content-hub"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Content Hub documentation
                      </a>{" "}
                      to learn how to enable other data sources.
                    </p>

                    {!isCompatibleGatsbyVersion && (
                      <p className={clsx(styles.hint)}>
                        This site uses an <b>unsupported Gatsby version</b>. To
                        use Content Hub, please upgrade to Gatsby{" "}
                        <b>{GATSBY_VERSION_FOR_DATA_LAYER}</b>.
                      </p>
                    )}

                    {(isFreePlan || !flags.acceleratedSourcingEarlyAccess) && (
                      <p className={styles.hint}>
                        {isFreePlan
                          ? `The feature is not available for Free plan.`
                          : `Content Hub is in Early Access.`}{" "}
                        <Link
                          to={`/dashboard/${organizationId}/sites/${siteId}/content-hub`}
                        >
                          Learn more
                        </Link>
                      </p>
                    )}
                  </>
                ) : (
                  <p className={styles.hint}>{noPermissionsMsg}</p>
                )
              }
              checked={dataLayerIsEnabled}
              disabled={dataLayerToggleDisabled}
              onChange={() => {
                handleDataLayerToggle()
              }}
            />

            {flags.acceleratedSourcingEarlyAccess && isCompatibleGatsbyVersion && (
              <ToggleCheckbox
                id="enable-data-layer-api-checkbox"
                label="Enable GraphQL API"
                hint={
                  permissionToEdit ? (
                    <p className={styles.hint}>
                      Create a public GraphQL API at gatsby.valhalla-api.io for
                      your content that can be used with SSR or other
                      frameworks.
                    </p>
                  ) : (
                    <p className={styles.hint}>{noPermissionsMsg}</p>
                  )
                }
                checked={apiIsEnabled}
                disabled={apiToggleDisabled}
                onChange={() => {
                  handleApiToggle()
                }}
                className={styles.apiToggle}
              />
            )}
          </>
        )}

        {errorAlert && <div className={styles.error}>{errorAlert}</div>}
      </div>

      {siteName && (
        <DataLayerApiConfirmation
          isOpen={isApiConfirmationModalOpen}
          onDismiss={() => setIsApiConfirmationModalOpen(false)}
          onConfirm={toggleApi}
          siteName={siteName}
        />
      )}
    </>
  )
}
